import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (backend/modules/pw9324/models/pw9324.go)
// ============================================================

/** Query filter parameters. POST /api/pw9324/query */
export interface QueryParams {
  year: number;         // PR_POST_YY — report year (e.g. 2024)
  dept_from?: string;   // L_START_32 — dept code start (empty = all)
  dept_to?: string;     // L_LAST_32  — dept code end (empty = all)
}

/** Leave-type metadata from F908 (BR-F02) */
export interface LeaveTypeInfo {
  code: string;  // F908_01 — leave type code C(2)
  name: string;  // composite: SUBSTR(F908_11,1,8) + "(" + F908_12 + ")"
  unit: number;  // F908_12: 1=日 2=時
}

/**
 * One aggregated row per employee (annual total across all 12 months).
 * Mirrors PF9140 structure from BR-C05.
 */
export interface AnnualLeaveSummaryRow {
  emp_code: string;   // F9140_01 / F9141_01
  dept_code: string;  // F9140_18 — from F901_32
  dept_name: string;  // from F106_11
  emp_name: string;   // from F901_11
  /**
   * leave_qty[i] = quantity for leave type at position i (1-based, index 0 unused).
   * BR-C05: up to 12 leave-type columns (F9140_201~F9140_212).
   */
  leave_qty: number[];  // length 13 (slots 1..12 used)
  h_tot: number;        // F9140_HTOT: total hours (日×8 + 時計)
  d_tot: number;        // F9140_DTOT: total days (日計 + 時計/8)
}

/** Full API response for POST /api/pw9324/query */
export interface QueryResult {
  leave_types: LeaveTypeInfo[];       // ordered leave-type column headers (up to 12)
  data: AnnualLeaveSummaryRow[];
  count: number;
  message?: string;  // "查無此資料!" when empty
}

/** Lookup response for GET /api/pw9324/lookup/:type/:code */
export interface LookupResponse {
  name: string;
}

// ============================================================
// API client
// ============================================================

export const pw9324Api = {
  /**
   * Query annual leave summary.
   * POST /api/pw9324/query
   */
  async query(params: QueryParams): Promise<QueryResult> {
    const res = await apiClient.post('/api/pw9324/query', params);
    return res.data;
  },

  /**
   * Lookup name for department or employee.
   * GET /api/pw9324/lookup/:type/:code
   * type: 'department' | 'employee'
   */
  async lookupName(type: 'department' | 'employee', code: string): Promise<string> {
    if (!code?.trim()) return '';
    try {
      const res = await apiClient.get(
        `/api/pw9324/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`,
      );
      return (res.data as LookupResponse).name || '';
    } catch {
      return '';
    }
  },
};
